import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShellLayout } from '../shell/useShellLayout';
import { usePdfViewerIdentity } from '../pdfViewer/usePdfViewerIdentity';
import { ActiveUsersBar } from '../realtime/ActiveUsersBar';
import { PatientKanbanBoard } from './PatientKanbanBoard';
import { PatientKanbanConfigPopup } from './PatientKanbanConfigPopup';
import { readKanbanLayout, writeKanbanLayout, type KanbanLayoutConfig } from './patientTrackingKanban';
import {
  PatientTrackerEntryEditor,
  type PatientTrackerEntryEditorHandle
} from './PatientTrackerEntryEditor';
import { PatientTrackerGrid } from './PatientTrackerGrid';
import { createPatientTrackingApi, patientTrackingApi } from './patientTrackingApi';
import { useLiveRows } from './useLiveRows';
import type { TrackerBoardLiveData } from './useLiveRows';
import {
  PatientTrackingBoardKeys,
  type CoveredEntityDto,
  type OurPharmacyDto,
  type PatientDto,
  type PatientTrackerFieldPolicy,
  type PatientTrackingCsvImportConfig,
  type TrackerEntryDto
} from './patientTrackingTypes';
import './patientTracking.css';

export interface PatientTrackerPageProps {
  /** Optional override for the current user id (defaults to the signed-in profile object id). */
  currentUserId?: string;
  /** API base path (defaults to full admin tracker API). */
  apiBase?: string;
  csvImport?: PatientTrackingCsvImportConfig;
  stateStorageKey?: string;
  fieldPolicy?: PatientTrackerFieldPolicy;
}

type ViewMode = 'grid' | 'kanban';

/**
 * CE patient communication tracker: full-bleed grid (grouped by status) or Kanban view, with
 * presence on the page toolbar and list/add actions on the grid toolbar.
 */
export function PatientTrackerPage({
  currentUserId,
  apiBase,
  csvImport,
  stateStorageKey = 'patient-tracker-grid',
  fieldPolicy
}: PatientTrackerPageProps) {
  const allowCreate = fieldPolicy?.allowCreate !== false;
  const { isNarrow } = useShellLayout();
  const api = useMemo(
    () => (apiBase ? createPatientTrackingApi(apiBase) : patientTrackingApi),
    [apiBase]
  );
  const identity = usePdfViewerIdentity(!currentUserId);
  const userId = currentUserId ?? identity.userId;
  const userName = identity.userName;
  const userAliases = identity.userAliases;

  const [view, setView] = useState<ViewMode>('grid');
  const [kanbanConfigOpen, setKanbanConfigOpen] = useState(false);
  const [kanbanLayout, setKanbanLayout] = useState<KanbanLayoutConfig>(() =>
    readKanbanLayout(stateStorageKey)
  );
  const [pharmacies, setPharmacies] = useState<OurPharmacyDto[]>([]);
  const [coveredEntities, setCoveredEntities] = useState<CoveredEntityDto[]>([]);
  const [patients, setPatients] = useState<PatientDto[]>([]);

  const live = useLiveRows<TrackerEntryDto>(PatientTrackingBoardKeys.tracker, api.listTrackerEntries);

  const loadCes = useCallback(async () => {
    try {
      setCoveredEntities(await api.listCoveredEntities());
    } catch {
      setCoveredEntities([]);
    }
  }, [api]);

  const loadPharmacies = useCallback(async () => {
    try {
      setPharmacies(await api.listPharmacies());
    } catch {
      setPharmacies([]);
    }
  }, [api]);

  const loadPatients = useCallback(async () => {
    try {
      setPatients(await api.listPatients());
    } catch {
      setPatients([]);
    }
  }, [api]);

  useEffect(() => {
    void loadPharmacies();
    void loadCes();
    void loadPatients();
  }, [loadCes, loadPatients, loadPharmacies]);

  const editorRef = useRef<PatientTrackerEntryEditorHandle>(null);

  const liveData: TrackerBoardLiveData = {
    rows: live.rows,
    loading: live.loading,
    error: live.error,
    reload: live.reload,
    applyUpsert: live.applyUpsert,
    rowEditors: live.rowEditors,
    beginEditRow: live.beginEditRow,
    endEditRow: live.endEditRow,
    setListEditing: live.setListEditing
  };

  return (
    <div className="ops-fill-page ops-fill-page--bare patient-tracker-page">
      <div className="patient-tracker-page__toolbar">
        <ActiveUsersBar roster={live.activeBarRoster} currentUserId={userId} editingLabel="Editing:" />
        <span className="patient-tracker-page__spacer" />
        {live.rowEditors.size > 0 ? (
          <span className="patient-tracker-page__lock-note">
            {live.rowEditors.size} row(s) being edited
          </span>
        ) : null}
        <div className="patient-tracker-page__view-controls">
          {view === 'kanban' ? (
            <>
              <button
                type="button"
                className="patient-tracker-page__kanban-action-btn"
                aria-label="Refresh kanban"
                title="Refresh"
                disabled={live.loading}
                onClick={() => void live.reload()}
              >
                <span className="dx-icon-refresh" aria-hidden />
                {isNarrow ? null : <span>Refresh</span>}
              </button>
              {allowCreate ? (
                <button
                  type="button"
                  className="patient-tracker-page__kanban-action-btn patient-tracker-page__kanban-action-btn--primary"
                  aria-label="Add issue"
                  title="Add issue"
                  onClick={() => editorRef.current?.openCreate()}
                >
                  <span className="dx-icon-add" aria-hidden />
                  {isNarrow ? null : <span>Add issue</span>}
                </button>
              ) : null}
            </>
          ) : null}
          {view === 'kanban' ? (
            <button
              type="button"
              className="patient-tracker-page__kanban-config-btn"
              aria-label="Kanban column settings"
              title="Kanban column settings"
              onClick={() => setKanbanConfigOpen(true)}
            >
              <span className="dx-icon-preferences" aria-hidden />
            </button>
          ) : null}
          <div className="patient-tracker-page__views" role="tablist" aria-label="View mode">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'grid'}
              className={`patient-tracker-page__view-btn${view === 'grid' ? ' patient-tracker-page__view-btn--active' : ''}`}
              onClick={() => setView('grid')}
            >
              Grid
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'kanban'}
              className={`patient-tracker-page__view-btn${view === 'kanban' ? ' patient-tracker-page__view-btn--active' : ''}`}
              onClick={() => setView('kanban')}
            >
              Kanban
            </button>
          </div>
        </div>
      </div>
      <div className="ops-fill-page__grid-host">
        {view === 'grid' ? (
          <PatientTrackerGrid
            coveredEntities={coveredEntities}
            onPatientsChanged={() => void loadPatients()}
            currentUserId={userId}
            stateStorageKey={stateStorageKey}
            csvImport={csvImport}
            api={api}
            onOpenView={(row) => editorRef.current?.openView(row)}
            onOpenCreate={() => editorRef.current?.openCreate()}
            fieldPolicy={fieldPolicy}
            live={liveData}
          />
        ) : (
          <PatientKanbanBoard
            live={liveData}
            api={api}
            layout={kanbanLayout}
            onLayoutChange={(next) => {
              setKanbanLayout(next);
              writeKanbanLayout(stateStorageKey, next);
            }}
            onOpenView={(row) => editorRef.current?.openView(row)}
          />
        )}
      </div>

      <PatientKanbanConfigPopup
        visible={kanbanConfigOpen}
        config={kanbanLayout}
        onClose={() => setKanbanConfigOpen(false)}
        onChange={(next) => {
          setKanbanLayout(next);
          writeKanbanLayout(stateStorageKey, next);
        }}
      />

      <PatientTrackerEntryEditor
        ref={editorRef}
        coveredEntities={coveredEntities}
        pharmacies={pharmacies}
        patients={patients}
        onPatientsChanged={() => void loadPatients()}
        currentUserId={userId}
        currentUserName={userName}
        currentUserAliases={userAliases}
        stateStorageKey={stateStorageKey}
        api={api}
        patientTrackingApiBase={apiBase}
        allowInlinePatientCreate={!apiBase && !fieldPolicy?.readOnlyPatient && allowCreate}
        fieldPolicy={fieldPolicy}
        live={liveData}
      />
    </div>
  );
}
